import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {View} from 'react-native';
import {images} from '../../../../assets/icons';
import {useStyles} from '../../../../shared/hooks/styles';
import {AcceptorBranchResponsePayload} from '../../../../shared/models/api';
import {useTheme} from '../../../../shared/theme';
import {formatPhone, formatPhoneToCallable} from '../../../../shared/pipes';
import {DeviceSize} from '../../../../shared/theme/helpers';
import {currentLocalitySelector} from '../../../../redux/selectors/localities';
import {YaMap} from '../../../../shared/components/yaMap';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {useDimensions} from '../../../../shared/hooks/useDimensions';
import {ViewProps} from './model';

interface Marker {
  id: number;
  payload: AcceptorBranchResponsePayload;
  coordinates: number[];
}

export default ({acceptorBranches, activeAcceptorBranch, onClickAcceptorBranch, acceptor, mapProps}: ViewProps) => {
  const theme = useTheme();
  const currentLocality = useSelector(currentLocalitySelector());
  const isDesktop = useIsDesktop();
  const {height} = useDimensions();
  const mapHeight = height * 0.5;

  const styles = useStyles(({addShadow, mediaOnly}) => ({
    container: {
      width: '100%',
      flexGrow: 0,
      flexShrink: 0,
      ...mediaOnly(DeviceSize.desktop, {
        borderRadius: 5,
        flexDirection: 'row',
        alignItems: 'stretch',
        ...addShadow({
          opacity: 0.05,
          width: 0,
          height: 4,
          radius: 10,
        }),
      }),
    },
    map: {
      width: '100%',
      minHeight: 300,
      ...mediaOnly(DeviceSize.desktop, {
        minHeight: 650,
        flexShrink: 1,
        flexGrow: 1,
      }),
    },
    image: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
    },
  }));

  const centerPoint = useMemo(() => currentLocality?.geolocation, [currentLocality]);

  const markers = useMemo(() => {
    const markersResult: Marker[] = [];

    acceptorBranches.forEach((item, i) => {
      const coordinates = item.geolocation?.map((coordinate) => Number(coordinate));
      if (coordinates != null && item.geolocation != null) {
        markersResult.push({
          id: coordinates[0] + coordinates[1] + i,
          payload: item,
          coordinates: item.geolocation,
        });
      }
    });

    return markersResult;
  }, [acceptorBranches]);

  const handleClickMarker = useCallback((data: any) => onClickAcceptorBranch(data.payload), [onClickAcceptorBranch]);
  const handleNullacceptorBranch = useCallback(() => onClickAcceptorBranch(null), [onClickAcceptorBranch]);

  const mapDetails = useMemo(() => {
    return [
      {
        title: activeAcceptorBranch?.name || acceptor?.name || '',
        info: [
          {
            text: activeAcceptorBranch?.address ?? '',
            icon: <images.Location color={theme.color.primaryColor} />,
            show: !!activeAcceptorBranch?.address,
          },
          {
            text: formatPhone(activeAcceptorBranch?.phone),
            href: `tel:${formatPhoneToCallable(activeAcceptorBranch?.phone)}`,
            icon: <images.Phone color={theme.color.primaryColor} />,
            show: !!activeAcceptorBranch?.phone,
          },
        ],
        show: !!activeAcceptorBranch,
      },

      {
        title: 'Часы работы',
        info: [
          {
            text: activeAcceptorBranch?.operatingMode ?? '',
            icon: <images.Clock2 color={theme.color.primaryColor} />,
          },
        ],
        show: !!activeAcceptorBranch?.operatingMode,
      },
    ];
  }, [activeAcceptorBranch]);

  return (
    <View style={styles.container}>
      <View style={[styles.map, !isDesktop && {height: mapHeight}]}>
        <YaMap
          key={acceptorBranches.length}
          markers={markers}
          centerPoint={centerPoint}
          onPressMarker={handleClickMarker}
          isDetailsVisible={!!activeAcceptorBranch}
          onCloseDetails={handleNullacceptorBranch}
          details={mapDetails}
          {...mapProps}
        />
      </View>
    </View>
  );
};
